import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n';
import '../PortfolioV2.css';

const content = {
  fr: {
    back: 'Retour au portfolio',
    kicker: 'Repères de budget',
    title: 'Tarif de création de site internet : des prix clairs, selon votre projet.',
    intro: 'Voici des prix pour des projets bien délimités, pas pour reproduire une plateforme entière. Un site où vos clients prennent rendez-vous, une boutique complète et un outil métier avec plusieurs modules n’impliquent pas le même travail.',
    cases: [
      ['Site vitrine', '850 € à 1 250 €', 'Présenter une activité, rassurer, être contacté.', ['Pages essentielles', 'Responsive', 'Formulaire', 'Mise en ligne']],
      ['Site avec prise de rendez-vous clients', '1 250 € à 1 750 €', 'Vos clients choisissent sur votre site un créneau pour une prestation et reçoivent une confirmation, sans gestion avancée.', ['Site essentiel', 'Créneaux', 'RDV clients', 'Confirmation']],
      ['Vente / paiement simple', '1 500 € à 2 200 €', 'Encaisser une prestation ou vendre quelques produits. Une grande boutique nécessite un chiffrage distinct.', ['Offre ou petit catalogue', 'Paiement', 'Suivi des commandes', 'Mise en ligne']],
      ['Outil métier ciblé', '1 550 € à 2 500 €+', 'Simplifier un processus ou une tâche précise, pas recréer un logiciel de gestion complet.', ['Interface simple', 'Base de données', 'Une logique métier définie', 'Administration']],
      ['Reprise de projet', '350 € à 850 €+', 'Débloquer ou terminer un projet déjà commencé.', ['Lecture de l’existant', 'Priorités', 'Corrections', 'Mise en ligne si utile']],
    ],
    fullKicker: 'Quand le projet va plus loin',
    fullTitle: 'Une plateforme complète ne se chiffre pas comme un petit site.',
    fullText: 'Boutique, rendez-vous pris par vos clients, paiements, factures, stock et espace de gestion peuvent vivre ensemble. Mais il faut comprendre vos règles métier, les connexions et les accès avant de fixer un prix. Je ne vous promets pas tout ça au tarif d’un site de départ.',
    fullCta: 'Parler de mon projet complet',
    changes: 'Ce qui change le budget',
    factors: ['Paiement ou acompte', 'Comptes utilisateurs et rôles', 'Connexion à un logiciel externe', 'Espace de gestion avancé', 'Imports / exports', 'Reprise de code existant'],
    ctaKicker: 'Votre projet ne rentre pas dans une case ?',
    ctaTitle: 'Faites l’estimation avec vos propres choix.',
    ctaText: 'Le résultat évolue selon les fonctionnalités sélectionnées et vous indique aussi ce qui peut attendre une deuxième version.',
    cta: 'Estimer mon projet',
  },
  en: {
    back: 'Back to portfolio',
    kicker: 'Budget references',
    title: 'Useful pricing should explain what makes it move.',
    intro: 'These are prices for a clearly defined first release, not for rebuilding a complete platform. A simple booking flow, a full shop and connected business tools require different scopes.',
    cases: [
      ['Business website', '€850 to €1,250', 'Present a business and turn visits into enquiries.', ['Essential pages', 'Responsive', 'Contact form', 'Launch']],
      ['Website with customer appointments', '€1,250 to €1,750', 'Customers choose a time for your services directly on your site and receive confirmation.', ['Essential website', 'Availability', 'Booking', 'Confirmation']],
      ['Simple sales / payments', '€1,500 to €2,200', 'Collect payments or sell a few products; a full shop needs its own quote.', ['Small catalogue', 'Payment', 'Order tracking', 'Launch']],
      ['Focused business tool', '€1,550 to €2,500+', 'Solve one clearly scoped process, not an entire operations suite.', ['Simple interface', 'Database', 'One defined workflow', 'Admin']],
      ['Project takeover', '€350 to €850+', 'Unblock or finish an existing project.', ['Existing review', 'Priorities', 'Fixes', 'Launch help']],
    ],
    fullKicker: 'For broader projects',
    fullTitle: 'A complete platform needs more than a starter-site estimate.',
    fullText: 'Shop, bookings, payments, invoices, stock and admin tools can work together. Defining data, access and integrations comes first, so a broader build needs a tailored quote.',
    fullCta: 'Discuss a complete project',
    changes: 'What changes the budget',
    factors: ['Payment or deposits', 'User accounts and roles', 'External integrations', 'Complex admin', 'Imports / exports', 'Existing code takeover'],
    ctaKicker: 'Your project does not fit a box?',
    ctaTitle: 'Estimate it with your own choices.',
    ctaText: 'The result changes with the features you select and points out what can wait until a second version.',
    cta: 'Estimate my project',
  },
  pt: {
    back: 'Voltar ao portfólio',
    kicker: 'Referências de orçamento',
    title: 'Um preço útil deve explicar o que faz ele mudar.',
    intro: 'Estes valores referem-se a uma primeira versão com escopo definido, não ao preço de uma plataforma completa. Reservas, loja e gestão integrada precisam de orçamentos diferentes.',
    cases: [
      ['Site institucional', '850 € a 1.250 €', 'Apresentar a atividade e receber contatos.', ['Páginas essenciais', 'Responsivo', 'Formulário', 'Publicação']],
      ['Site com agendamento de clientes', '1.250 € a 1.750 €', 'Os clientes escolhem no seu site um horário para o serviço e recebem confirmação.', ['Site essencial', 'Disponibilidade', 'Reserva', 'Confirmação']],
      ['Venda / pagamento simples', '1.500 € a 2.200 €', 'Receber pagamentos ou vender poucos produtos. Uma loja completa exige orçamento próprio.', ['Catálogo simples', 'Pagamento', 'Pedidos', 'Publicação']],
      ['Ferramenta de gestão focada', '1.550 € a 2.500 €+', 'Simplificar um processo concreto, não desenvolver uma plataforma inteira.', ['Interface simples', 'Base de dados', 'Processo definido', 'Administração']],
      ['Retomada', '350 € a 850 €+', 'Destravar ou terminar um projeto.', ['Leitura do existente', 'Prioridades', 'Correções', 'Publicação']],
    ],
    fullKicker: 'Projetos maiores',
    fullTitle: 'Uma plataforma completa não custa o mesmo que um site simples.',
    fullText: 'Loja, reservas, pagamentos, faturas, stock e gestão podem funcionar juntos. Mas é preciso definir os processos, dados e integrações antes de fazer o orçamento.',
    fullCta: 'Falar sobre um projeto completo',
    changes: 'O que muda o orçamento',
    factors: ['Pagamento ou sinal', 'Contas e perfis', 'Integrações externas', 'Admin complexo', 'Imports / exports', 'Código existente'],
    ctaKicker: 'Seu projeto não cabe em uma caixa?',
    ctaTitle: 'Faça a estimativa com suas próprias escolhas.',
    ctaText: 'O resultado muda conforme as funções escolhidas e mostra o que pode ficar para uma segunda versão.',
    cta: 'Estimar meu projeto',
  },
};

export default function PricingV2() {
  const { lang } = useLang();
  const t = content[lang] || content.fr;

  useEffect(() => {
    document.title = lang === 'fr' ? 'Tarif création site internet freelance | Alioune Seck' : 'Freelance web development pricing | Alioune Seck';
  }, [lang]);

  return (
    <div className="pricing-v2 estimator-page-v2">
      <header className="estimator-page-header">
        <Link to="/" className="v2-brand"><span className="v2-brand-symbol">AS</span><span><strong>Alioune Seck</strong><small>Développeur web freelance</small></span></Link>
        <Link to="/" className="v2-secondary">← {t.back}</Link>
      </header>

      <main className="pricing-v2-main">
        <section className="pricing-v2-hero">
          <span className="v2-kicker">{t.kicker}</span>
          <h1>{t.title}</h1>
          <p>{t.intro}</p>
        </section>

        <section className="pricing-case-grid">
          {t.cases.map(([title, price, desc, items], index) => (
            <article key={title}>
              <div className="pricing-case-top"><span>0{index + 1}</span><strong>{price}</strong></div>
              <h2>{title}</h2>
              <p>{desc}</p>
              <ul>{items.map((item) => <li key={item}>{item}</li>)}</ul>
            </article>
          ))}
        </section>

        <section className="pricing-complete-scope" aria-labelledby="pricing-complete-title">
          <span className="v2-kicker">{t.fullKicker}</span>
          <h2 id="pricing-complete-title">{t.fullTitle}</h2>
          <p>{t.fullText}</p>
          <Link className="v2-primary" to="/#contact">{t.fullCta} →</Link>
        </section>

        <section className="pricing-factors">
          <span className="v2-kicker">{t.changes}</span>
          <div>{t.factors.map((factor) => <span key={factor}>{factor}</span>)}</div>
        </section>

        <section className="pricing-estimator-cta">
          <span className="v2-kicker">{t.ctaKicker}</span>
          <h2>{t.ctaTitle}</h2>
          <p>{t.ctaText}</p>
          <Link className="v2-primary" to="/estimer-mon-projet">{t.cta} →</Link>
        </section>
      </main>
    </div>
  );
}
